import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DsModal from './DsModal';

function textFromNode(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textFromNode).join('');
  if (React.isValidElement(node)) return textFromNode(node.props.children);
  return '';
}

function flattenOptions(children) {
  const options = [];
  React.Children.forEach(children, (child) => {
    if (child == null || typeof child === 'boolean') return;
    if (Array.isArray(child)) {
      options.push(...flattenOptions(child));
      return;
    }
    if (React.isValidElement(child) && child.type === React.Fragment) {
      options.push(...flattenOptions(child.props.children));
      return;
    }
    if (!React.isValidElement(child) || child.type !== 'option') return;
    const label = textFromNode(child.props.children).trim();
    const rawValue = child.props.value ?? label;
    options.push({
      value: String(rawValue),
      label,
      node: child.props.children,
      disabled: Boolean(child.props.disabled),
      hidden: Boolean(child.props.hidden),
    });
  });
  return options;
}

export default function DsSelect({
  children,
  className = '',
  value,
  defaultValue,
  onChange,
  disabled = false,
  name,
  id,
  title,
  required,
  placeholder,
  style,
  onBlur,
  onFocus,
  presentation = 'popover',
  modalTitle = '選択してください',
  ...rest
}) {
  const generatedId = useId();
  const buttonId = id || `ds-select-${generatedId}`;
  const listboxId = `${buttonId}-listbox`;
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const optionRefs = useRef([]);
  const [open, setOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const modalPresentation = presentation === 'modal';
  const options = useMemo(() => flattenOptions(children), [children]);
  const controlled = value !== undefined;
  const currentValue = String(controlled ? value ?? '' : uncontrolledValue ?? options[0]?.value ?? '');
  const selectedIndex = options.findIndex((option) => option.value === currentValue);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;
  const visibleOptions = useMemo(() => options.filter((option) => !option.hidden), [options]);
  const activeOption = visibleOptions[activeIndex] || selectedOption || visibleOptions.find((option) => !option.disabled);

  const updateMenuPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewportPadding = 8;
    const maxHeight = Math.max(160, Math.min(320, window.innerHeight - rect.bottom - viewportPadding));
    // トリガーが細い（表示件数など）場合でも選択肢が切れないよう、最低幅を確保しつつ
    // 内容に応じて広がるようにする（幅はCSSの max-content 相当で自動）。
    const menuMinWidth = Math.min(
      Math.max(rect.width, 176),
      window.innerWidth - viewportPadding * 2,
    );
    setMenuStyle({
      position: 'fixed',
      left: `${Math.max(viewportPadding, Math.min(rect.left, window.innerWidth - menuMinWidth - viewportPadding))}px`,
      top: `${rect.bottom + 4}px`,
      minWidth: `${menuMinWidth}px`,
      maxWidth: `${window.innerWidth - viewportPadding * 2}px`,
      maxHeight: `${maxHeight}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open || modalPresentation) return;
    updateMenuPosition();
  }, [modalPresentation, open, updateMenuPosition]);

  useEffect(() => {
    if (!open || modalPresentation) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (event.target?.closest?.(`[data-ds-select-listbox="${listboxId}"]`)) return;
      setOpen(false);
    };
    const handleReposition = () => updateMenuPosition();
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [listboxId, modalPresentation, open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const nextIndex = Math.max(0, visibleOptions.findIndex((option) => option.value === currentValue));
    setActiveIndex(nextIndex);
  }, [currentValue, open, visibleOptions]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const emitChange = useCallback(
    (nextValue) => {
      if (!controlled) setUncontrolledValue(nextValue);
      const target = { value: nextValue, name, id: buttonId };
      onChange?.({
        target,
        currentTarget: target,
        preventDefault: () => {},
        stopPropagation: () => {},
        persist: () => {},
      });
    },
    [buttonId, controlled, name, onChange],
  );

  const selectOption = useCallback(
    (option) => {
      if (!option || option.disabled) return;
      emitChange(option.value);
      setOpen(false);
      if (!modalPresentation) buttonRef.current?.focus();
    },
    [emitChange, modalPresentation],
  );

  const moveActive = useCallback(
    (direction) => {
      if (visibleOptions.length === 0) return;
      setOpen(true);
      setActiveIndex((current) => {
        let next = current < 0 ? 0 : current;
        for (let i = 0; i < visibleOptions.length; i += 1) {
          next = (next + direction + visibleOptions.length) % visibleOptions.length;
          if (!visibleOptions[next]?.disabled) return next;
        }
        return current;
      });
    },
    [visibleOptions],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (disabled) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveActive(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        const first = visibleOptions.findIndex((option) => !option.disabled);
        if (first >= 0) {
          setOpen(true);
          setActiveIndex(first);
        }
      } else if (event.key === 'End') {
        event.preventDefault();
        const reversed = [...visibleOptions].reverse().findIndex((option) => !option.disabled);
        if (reversed >= 0) {
          setOpen(true);
          setActiveIndex(visibleOptions.length - 1 - reversed);
        }
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          selectOption(activeOption);
        }
      } else if (event.key === 'Escape') {
        setOpen(false);
      }
    },
    [activeOption, disabled, moveActive, open, selectOption, visibleOptions],
  );

  const rootClassName = ['ds-select', modalPresentation ? 'ds-select--modal' : '', open ? 'is-open' : '', disabled ? 'is-disabled' : '', className]
    .filter(Boolean)
    .join(' ');
  const displayNode = selectedOption?.node ?? selectedOption?.label ?? placeholder ?? '';
  const optionButtons = visibleOptions.map((option, index) => {
    const selected = option.value === currentValue;
    const active = activeOption?.value === option.value;
    return (
      <button
        key={`${option.value}-${index}`}
        ref={(node) => { optionRefs.current[index] = node; }}
        type="button"
        id={`${listboxId}-option-${index}`}
        className={`ds-select__option${selected ? ' is-selected' : ''}${active ? ' is-active' : ''}`}
        role="option"
        aria-selected={selected}
        disabled={option.disabled}
        onClick={() => selectOption(option)}
        onFocus={() => setActiveIndex(index)}
        onMouseEnter={() => setActiveIndex(index)}
        onKeyDown={modalPresentation ? (event) => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const enabled = visibleOptions.map((item, i) => item.disabled ? -1 : i).filter(i => i >= 0);
          const position = enabled.indexOf(index);
          const next = event.key === 'Home' ? enabled[0] : event.key === 'End' ? enabled[enabled.length - 1]
            : enabled[(position + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length];
          optionRefs.current[next]?.focus();
        } : undefined}
      >
        <span className="ds-select__option-check" aria-hidden="true">{selected ? '✓' : ''}</span>
        <span className="ds-select__option-label">{option.node ?? option.label}</span>
      </button>
    );
  });

  return (
    <span className={rootClassName} style={style} ref={rootRef} data-required={required ? '' : undefined}>
      {name ? <input type="hidden" name={name} value={currentValue} required={required} /> : null}
      <button
        {...rest}
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="ds-select__button"
        title={title}
        disabled={disabled}
        aria-haspopup={modalPresentation ? 'dialog' : 'listbox'}
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={!modalPresentation && open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        onBlur={onBlur}
        onFocus={onFocus}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        onKeyDown={handleKeyDown}
      >
        <span className="ds-select__value">{displayNode}</span>
        <i className="fa-solid fa-chevron-down ds-select__icon" aria-hidden="true" />
      </button>
      {modalPresentation ? <DsModal open={open} onClose={() => setOpen(false)} title={modalTitle} size="sm">
        <div id={listboxId} className="ds-select__modal-options" role="listbox" aria-label={modalTitle}>
          {optionButtons}
        </div>
      </DsModal> : open && menuStyle
        ? createPortal(
            <div
              id={listboxId}
              className="ds-select__listbox"
              role="listbox"
              style={menuStyle}
              data-ds-select-listbox={listboxId}
            >
              {optionButtons}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
